import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { FormSection, FormColumn, ConceptTree } from '../schema/types';
import './concept-tree';
import './add-column-dialog';
import { AddColumnRequest, AvailableDimension } from './add-column-dialog';

@customElement('jupiter-form-section')
export class JupiterFormSection extends LitElement {
  @property({ type: Object }) section!: FormSection;
  @property({ type: Array }) columns: FormColumn[] = [];
  @property({ type: Object }) formData: Record<string, any> = {};
  @property({ type: Object }) typedMemberData: Record<string, Record<string, string>> = {};
  @property({ type: Boolean }) disabled = false;
  @property({ type: Boolean }) collapsible = true;
  @property({ type: String }) locale = 'en-US';
  @property({ type: Boolean }) isFirstSection = false;
  @property({ type: Array }) availableDimensions: AvailableDimension[] = [];

  @state() private _expanded = true;
  @state() private _showAddColumnDialog = false;
  @state() private _sectionPeriodType: 'duration' | 'instant' | 'mixed' = 'duration';

  static styles = css`
    :host {
      display: block;
      margin-bottom: 16px;
      border: 1px solid var(--jupiter-border-color, #ddd);
      border-radius: 6px;
      overflow: hidden;
    }

    .section-header {
      background: var(--jupiter-section-header-background, #f0f2f5);
      padding: 16px;
      border-bottom: 1px solid var(--jupiter-border-color, #ddd);
      cursor: pointer;
      user-select: none;
      display: flex;
      align-items: center;
      justify-content: space-between;
    }

    .section-header:hover {
      background: var(--jupiter-section-header-hover-background, #e4e7ea);
    }

    .section-header.not-collapsible {
      cursor: default;
    }

    .section-header.not-collapsible:hover {
      background: var(--jupiter-section-header-background, #f0f2f5);
    }

    .section-title {
      font-size: 18px;
      font-weight: 600;
      color: var(--jupiter-text-primary, #333);
      margin: 0;
    }

    .section-description {
      font-size: 14px;
      color: var(--jupiter-text-secondary, #666);
      margin: 4px 0 0 0;
    }

    .section-toggle {
      width: 24px;
      height: 24px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 16px;
      transition: transform 0.2s ease;
      color: var(--jupiter-text-secondary, #666);
    }

    .section-toggle.expanded {
      transform: rotate(90deg);
    }

    .section-toggle.hidden {
      visibility: hidden;
    }

    .section-header-controls {
      display: flex;
      align-items: center;
      gap: 12px;
    }

    .tree-control {
      display: flex;
      align-items: center;
      gap: 6px;
      font-size: 12px;
      color: var(--jupiter-text-secondary, #666);
      background: var(--jupiter-button-background, #fff);
      border: 1px solid var(--jupiter-border-color, #ddd);
      border-radius: 4px;
      padding: 4px 8px;
      cursor: pointer;
      transition: all 0.2s ease;
    }

    .tree-control:hover {
      background: var(--jupiter-button-hover-background, #f5f5f5);
      border-color: var(--jupiter-primary-color, #007bff);
    }

    .tree-control-checkbox {
      width: 14px;
      height: 14px;
      cursor: pointer;
    }

    .tree-control-label {
      cursor: pointer;
      font-weight: 500;
      white-space: nowrap;
    }

    .section-content {
      display: block;
      transition: max-height 0.3s ease;
      overflow: hidden;
    }

    .section-content.collapsed {
      display: none;
    }

    .table-container {
      overflow-x: auto;
    }

    .form-table {
      width: 100%;
      border-collapse: separate;
      border-spacing: 0;
      background: var(--jupiter-table-background, #fff);
    }

    .table-header {
      background: var(--jupiter-header-background, #f8f9fa);
      position: sticky;
      top: 0;
      z-index: 1;
    }

    .header-row {
      /* Table row - no special display needed */
    }

    .header-cell {
      padding: 8px 12px;
      text-align: left;
      font-weight: 600;
      color: var(--jupiter-text-primary, #333);
      border: 1px solid var(--jupiter-border-color, #ddd);
      background: var(--jupiter-header-background, #f8f9fa);
      min-width: 180px;
      width: 180px;
      font-size: 14px;
      vertical-align: middle;
    }

    .header-cell.concept-column {
      width: 300px;
      min-width: 300px;
      max-width: 300px;
      position: sticky;
      left: 0;
      z-index: 2;
    }

    .header-cell.removable {
      position: relative;
    }

    .remove-column-btn {
      position: absolute;
      top: 4px;
      right: 4px;
      width: 20px;
      height: 20px;
      border: none;
      background: var(--jupiter-error-color, #d32f2f);
      color: white;
      border-radius: 50%;
      font-size: 12px;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .remove-column-btn:hover {
      background: var(--jupiter-error-color-dark, #b71c1c);
    }

    .add-column-btn {
      background: var(--jupiter-primary-color, #667eea);
      color: white;
      border: none;
      padding: 8px 16px;
      border-radius: 4px;
      cursor: pointer;
      font-size: 12px;
      margin: 8px;
    }

    .add-column-btn:hover {
      background: var(--jupiter-primary-color-dark, #5a6fd8);
    }

    /* Column header content styling */
    .column-header-content {
      display: flex;
      flex-direction: column;
      gap: 8px;
      align-items: center;
    }

    .column-title {
      text-align: center;
    }

    /* Typed member header input styles */
    .typed-members-header {
      display: flex;
      flex-direction: column;
      gap: 4px;
      width: 100%;
      padding: 8px;
      background: var(--jupiter-typed-member-header-background, #f8f9fa);
      border-radius: 4px;
      border: 1px solid var(--jupiter-border-color, #e0e0e0);
    }

    .typed-member-header-input {
      display: flex;
      flex-direction: column;
      gap: 2px;
    }

    .typed-member-header-label {
      font-size: 11px;
      font-weight: 500;
      color: var(--jupiter-text-secondary, #666);
      text-align: left;
    }

    .typed-member-header-field {
      width: 100%;
      padding: 4px 6px;
      border: 1px solid var(--jupiter-border-color, #ddd);
      border-radius: 3px;
      font-size: 12px;
      background: var(--jupiter-input-background, #fff);
    }

    .typed-member-header-field:focus {
      outline: none;
      border-color: var(--jupiter-primary-color, #1976d2);
      box-shadow: 0 0 0 1px var(--jupiter-primary-color, #1976d2);
    }

    .typed-member-header-field::placeholder {
      color: var(--jupiter-text-placeholder, #999);
      font-size: 11px;
    }

    .empty-section {
      padding: 24px;
      text-align: center;
      color: var(--jupiter-text-secondary, #666);
      font-style: italic;
    }
  `;

  connectedCallback() {
    super.connectedCallback();
    this._expanded = this.section.expanded !== false;
    this._determinePeriodType();
    this._checkAndExpandFirstSection();
  }
  
  updated(changedProperties: Map<string, any>) {
    super.updated(changedProperties);
    
    // Check if this became the first section or if section/isFirstSection properties changed
    if (changedProperties.has('isFirstSection') || changedProperties.has('section')) {
      this._checkAndExpandFirstSection();
    }
  }
  
  private _checkAndExpandFirstSection() {
    // If this is the first section, expand it and its tree by default
    if (this.isFirstSection && this.section && this.section.concepts) {
      this._expanded = true;
      // Use setTimeout to ensure the DOM is ready for tree expansion
      setTimeout(() => {
        this._expandAllTrees();
        console.log('🎯 First section detected - expanded section and tree by default');
      }, 0);
    }
  }

  private _determinePeriodType() {
    // Get all non-abstract concepts
    const nonAbstractConcepts = this._getAllNonAbstractConcepts(this.section.concepts);
    
    if (nonAbstractConcepts.length === 0) {
      this._sectionPeriodType = 'duration';
      console.log(`📊 Section ${this.section.id}: No concepts, defaulting to duration`);
      return;
    }

    // Check period types
    const periodTypes = new Set(nonAbstractConcepts.map(c => c.periodType).filter(Boolean));
    
    console.log(`🏷️ Section ${this.section.id}: Found period types:`, Array.from(periodTypes));
    console.log(`📝 Section ${this.section.id}: Non-abstract concepts and their period types:`, 
      nonAbstractConcepts.map(c => ({ name: c.name, periodType: c.periodType, abstract: c.abstract }))
    );
    
    if (periodTypes.size === 0) {
      this._sectionPeriodType = 'duration';
      console.log(`📊 Section ${this.section.id}: No period types defined, defaulting to duration`);
    } else if (periodTypes.size === 1) {
      const singleType = Array.from(periodTypes)[0];
      this._sectionPeriodType = singleType === 'instant' ? 'instant' : 'duration';
      console.log(`📊 Section ${this.section.id}: Single period type '${singleType}', setting to '${this._sectionPeriodType}'`);
    } else {
      this._sectionPeriodType = 'mixed';
      console.log(`📊 Section ${this.section.id}: Mixed period types, setting to 'mixed'`);
    }
    
    console.log(`✅ Section ${this.section.id}: Final period type: ${this._sectionPeriodType}`);
  }

  private _getAllNonAbstractConcepts(concepts: ConceptTree[]): ConceptTree[] {
    const result: ConceptTree[] = [];
    
    for (const concept of concepts) {
      if (!concept.abstract) {
        result.push(concept);
      }
      if (concept.children) {
        result.push(...this._getAllNonAbstractConcepts(concept.children));
      }
    }
    
    return result;
  }

  private _toggleExpanded() {
    if (!this.collapsible) return;
    
    this._expanded = !this._expanded;
    
    this.dispatchEvent(new CustomEvent('section-expand', {
      detail: {
        sectionId: this.section.id,
        expanded: this._expanded
      },
      bubbles: true
    }));
  }

  private _handleRemoveColumn(columnId: string) {
    this.dispatchEvent(new CustomEvent('column-remove', {
      detail: { 
        columnId,
        sectionId: this.section.id 
      },
      bubbles: true
    }));
  }

  private _handleAddColumn() {
    // Determine period type specifically for this add column action
    const buttonPeriodType = this._determineButtonPeriodType();
    
    // Override the section period type for this dialog
    this._sectionPeriodType = buttonPeriodType;
    this._showAddColumnDialog = true;
  }

  private _determineButtonPeriodType(): 'duration' | 'instant' | 'mixed' {
    // Get all non-abstract concepts in the section (not just those with fields)
    // This ensures we correctly identify mixed period sections even when some concepts
    // don't have fields in the default column due to period type filtering
    const allNonAbstractConcepts = this._getAllNonAbstractConcepts(this.section.concepts);
    
    if (allNonAbstractConcepts.length === 0) {
      return 'duration';
    }

    // Check period types of all non-abstract concepts in the section
    const periodTypes = new Set(
      allNonAbstractConcepts
        .map(c => c.periodType)
        .filter(periodType => periodType !== undefined && periodType !== null)
    );
    
    if (periodTypes.size === 0) {
      return 'duration';
    } else if (periodTypes.size === 1) {
      const singleType = Array.from(periodTypes)[0];
      return singleType === 'instant' ? 'instant' : 'duration';
    } else {
      return 'mixed';
    }
  }

  private _getConceptsWithFields(concepts: ConceptTree[]): ConceptTree[] {
    const result: ConceptTree[] = [];
    
    for (const concept of concepts) {
      // Include concepts that have fields and are not abstract
      if (!concept.abstract && concept.fields && concept.fields.length > 0) {
        result.push(concept);
      }
      
      // Recursively check children
      if (concept.children) {
        result.push(...this._getConceptsWithFields(concept.children));
      }
    }
    
    return result;
  }

  private _handleDialogCancel() {
    this._showAddColumnDialog = false;
  }

  private _handleColumnAdd(event: CustomEvent<AddColumnRequest>) {
    event.stopPropagation();
    this._showAddColumnDialog = false;
    
    // Dispatch event to parent component with the column request
    this.dispatchEvent(new CustomEvent('column-add-request', {
      detail: {
        sectionId: this.section.id,
        columnRequest: event.detail
      },
      bubbles: true
    }));
  }

  private _handleFieldChange(event: CustomEvent) {
    event.stopPropagation();
    this.dispatchEvent(new CustomEvent('field-change', {
      detail: event.detail,
      bubbles: true
    }));
  }

  private _flattenConcepts(concepts: ConceptTree[], expanded: Set<string> = new Set()): ConceptTree[] {
    const result: ConceptTree[] = [];
    
    for (const concept of concepts) {
      result.push(concept);
      
      // If concept is expanded and has children, include them
      if (concept.children && concept.children.length > 0 && expanded.has(concept.id)) {
        const childrenFlattened = this._flattenConcepts(concept.children, expanded);
        result.push(...childrenFlattened);
      }
    }
    
    return result;
  }

  @state() private _expandedConcepts = new Set<string>();
  @state() private _allTreeExpanded = false;

  private _getAllConceptIds(concepts: ConceptTree[]): string[] {
    const result: string[] = [];
    
    for (const concept of concepts) {
      if (concept.children && concept.children.length > 0) {
        result.push(concept.id);
        result.push(...this._getAllConceptIds(concept.children));
      }
    }
    
    return result;
  }

  private _expandAllTrees() {
    const allConceptIds = this._getAllConceptIds(this.section.concepts || []);
    allConceptIds.forEach(id => this._expandedConcepts.add(id));
    this._allTreeExpanded = true;
    this.requestUpdate();
  }

  private _collapseAllTrees() {
    this._expandedConcepts.clear();
    this._allTreeExpanded = false;
    this.requestUpdate();
  }

  private _toggleAllTrees(event: Event) {
    event.stopPropagation(); // Prevent triggering accordion toggle
    
    // Auto-expand the accordion if it's collapsed
    if (!this._expanded) {
      this._expanded = true;
    }
    
    if (this._allTreeExpanded) {
      this._collapseAllTrees();
    } else {
      this._expandAllTrees();
    }
  }

  private _handleTreeCheckboxChange(event: Event) {
    event.stopPropagation(); // Prevent triggering accordion toggle
    const checkbox = event.target as HTMLInputElement;
    
    // Auto-expand the accordion if it's collapsed
    if (!this._expanded) {
      this._expanded = true;
    }
    
    if (checkbox.checked) {
      this._expandAllTrees();
    } else {
      this._collapseAllTrees();
    }
  }

  private _handleConceptExpand(event: CustomEvent) {
    event.stopPropagation();
    const { conceptId, expanded } = event.detail;
    
    if (expanded) {
      this._expandedConcepts.add(conceptId);
    } else {
      this._expandedConcepts.delete(conceptId);
    }
    
    // Update the allTreeExpanded state based on whether all concepts are expanded
    const allConceptIds = this._getAllConceptIds(this.section.concepts || []);
    this._allTreeExpanded = allConceptIds.length > 0 && allConceptIds.every(id => this._expandedConcepts.has(id));
    
    // Force re-render
    this.requestUpdate();
    
    this.dispatchEvent(new CustomEvent('concept-expand', {
      detail: event.detail,
      bubbles: true
    }));
  }

  private _getTypedMemberHeaderValue(columnId: string, axisId: string): string {
    // Get typed member value from the dedicated typed member data structure
    return this.typedMemberData[columnId]?.[axisId] || '';
  }

  private _handleTypedMemberHeaderChange(event: Event, columnId: string, axisId: string) {
    const target = event.target as HTMLInputElement;
    const value = target.value;
    
    console.log(`🔍 [FormSection] Typed member header change: columnId=${columnId}, axisId=${axisId}, value=${value}`);
    
    // Dispatch typed member change event (matches what dynamic form expects)
    this.dispatchEvent(new CustomEvent('typed-member-change', {
      detail: {
        columnId: columnId,
        axisId: axisId,
        value: value
      },
      bubbles: true
    }));
  }

  render() {
    if (!this.section.concepts || this.section.concepts.length === 0) {
      return html`
        <div class="section-header ${!this.collapsible ? 'not-collapsible' : ''}" 
             @click="${this._toggleExpanded}">
          <div>
            <h3 class="section-title">${this.section.title}</h3>
            ${this.section.description ? html`
              <p class="section-description">${this.section.description}</p>
            ` : ''}
          </div>
          <div class="section-header-controls">
            <div class="section-toggle ${this.collapsible ? (this._expanded ? 'expanded' : '') : 'hidden'}">
              ${this.collapsible ? '▶' : ''}
            </div>
          </div>
        </div>
        
        <div class="section-content ${!this._expanded ? 'collapsed' : ''}">
          <div class="empty-section">
            No concepts defined for this section.
          </div>
        </div>
      `;
    }

    return html`
      <div class="section-header ${!this.collapsible ? 'not-collapsible' : ''}" 
           @click="${this._toggleExpanded}">
        <div>
          <h3 class="section-title">${this.section.title}</h3>
          ${this.section.description ? html`
            <p class="section-description">${this.section.description}</p>
          ` : ''}
        </div>
        <div class="section-header-controls">
          <div class="tree-control">
            <input
              type="checkbox"
              class="tree-control-checkbox"
              .checked="${this._allTreeExpanded}"
              @change="${this._handleTreeCheckboxChange}"
            />
            <span class="tree-control-label" @click="${this._toggleAllTrees}">${this._allTreeExpanded ? 'Collapse Tree' : 'Expand Tree'}</span>
          </div>
          <div class="section-toggle ${this.collapsible ? (this._expanded ? 'expanded' : '') : 'hidden'}">
            ${this.collapsible ? '▶' : ''}
          </div>
        </div>
      </div>

      <div class="section-content ${!this._expanded ? 'collapsed' : ''}">
        <div class="table-container">
          <table class="form-table">
            <thead class="table-header">
              <tr class="header-row">
                <th class="header-cell concept-column">Concept</th>
                ${this.columns.map(column => html`
                  <th class="header-cell ${column.removable ? 'removable' : ''}">
                    <div class="column-header-content">
                      <div class="column-title">
                        ${column.title}
                        ${column.description ? html`<div style="font-weight: normal; font-size: 12px; color: var(--jupiter-text-secondary, #666);">${column.description}</div>` : ''}
                      </div>
                      
                      <!-- Typed member input fields in column header -->
                      ${column.dimensionData?.hasTypedMembers ? html`
                        <div class="typed-members-header">
                          ${column.dimensionData?.typedMembers?.map((typedMember: any) => html`
                            <div class="typed-member-header-input">
                              <input
                                type="text"
                                class="typed-member-header-field"
                                placeholder="Enter ${typedMember.axisLabel}"
                                .value="${this._getTypedMemberHeaderValue(column.id, typedMember.axisId)}"
                                @input="${(e: Event) => this._handleTypedMemberHeaderChange(e, column.id, typedMember.axisId)}"
                              />
                            </div>
                          `)}
                        </div>
                      ` : ''}
                      
                      ${column.removable ? html`
                        <button 
                          class="remove-column-btn"
                          @click="${(e: Event) => {
                            e.stopPropagation();
                            this._handleRemoveColumn(column.id);
                          }}"
                          title="Remove column"
                        >×</button>
                      ` : ''}
                    </div>
                  </th>
                `)}
                <th class="header-cell">
                  <button class="add-column-btn" @click="${(e: Event) => {
                    e.stopPropagation();
                    this._handleAddColumn();
                  }}" title="Add Column">
                    + Add Column
                  </button>
                </th>
              </tr>
            </thead>
            <tbody class="table-body">
              ${this._flattenConcepts(this.section.concepts, this._expandedConcepts).map(concept => html`
                <tr>
                  <jupiter-concept-tree
                    .concept="${concept}"
                    .columns="${this.columns}"
                    .formData="${this.formData}"
                    .disabled="${this.disabled}"
                    .locale="${this.locale}"
                    .expandedConcepts="${this._expandedConcepts}"
                    @field-change="${this._handleFieldChange}"
                    @concept-expand="${this._handleConceptExpand}"
                  ></jupiter-concept-tree>
                </tr>
              `)}
            </tbody>
          </table>
        </div>
      </div>

      <!-- Add Column Dialog -->
      <jupiter-add-column-dialog
        .periodType="${this._sectionPeriodType}"
        ?open="${this._showAddColumnDialog}"
        .availableDimensions="${this.availableDimensions}"
        @dialog-cancel="${this._handleDialogCancel}"
        @column-add="${this._handleColumnAdd}"
      ></jupiter-add-column-dialog>
    `;
  }
}
